import React, { useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import ActionLinks from '../components/ActionLinks';
import FilterCard from '../components/FilterCard';
import Pagination from '../components/Pagination';
import TableLayout from '../components/TableLayout';

// 我的互评任务页面（学生端）
// 页面结构：顶部状态 Tab + 条件筛选卡片 + 提示条 + 结果表格 + 分页
export default function StudentPeerReviewPage() {
  // ================= 页面状态 =================
  const [activeTab, setActiveTab] = useState('全部');
  const [scoreTask, setScoreTask] = useState(null);

  // ================= Tab 配置 =================
  const tabs = ['全部', '待评分', '已评分'];

  // ================= 条件筛选配置 =================
  const filterFields = [
    { label: '学年', type: 'select', options: ['全部', '2025-2026', '2024-2025'] },
    { label: '学期', type: 'select', options: ['全部', '第一学期', '第二学期'] },
    { label: '批次', type: 'select', options: ['全部', '2025秋季批次'] },
    { label: '学院', type: 'select', options: ['全部', '计算机学院', '文学院'] },
    { label: '班级', type: 'select', options: ['全部', '计算机1班', '计算机2班'] },
    { label: '被评人学号', type: 'input', placeholder: '请输入学号' },
    { label: '被评人姓名', type: 'input', placeholder: '请输入姓名' },
  ];

  // ================= 页面数据 =================
  const mockTasks = [
    {
      id: 1,
      year: '2025-2026',
      semester: '一',
      batch: '2025秋季批次',
      name: '李思思',
      studentId: '2102008',
      className: '计算机1班',
      college: '计算机学院',
      grade: '2021级',
      score: null,
      status: '待评分',
    },
    {
      id: 2,
      year: '2025-2026',
      semester: '一',
      batch: '2025秋季批次',
      name: '王大锤',
      studentId: '2102015',
      className: '计算机1班',
      college: '计算机学院',
      grade: '2021级',
      score: 4.5,
      status: '已评分',
    },
    {
      id: 3,
      year: '2025-2026',
      semester: '一',
      batch: '2025秋季批次',
      name: '赵小云',
      studentId: '2102022',
      className: '计算机1班',
      college: '计算机学院',
      grade: '2021级',
      score: null,
      status: '待评分',
    },
  ];

  // ================= 查询逻辑 =================
  const filteredTasks = activeTab === '全部'
    ? mockTasks
    : mockTasks.filter(task => task.status === activeTab);

  // ================= 表格配置 =================
  const tableHeaders = [
    '学年',
    '学期',
    '批次',
    '被评价人姓名',
    '被评价人学号',
    '班级',
    '学院',
    '年级',
    '评分',
    '状态',
    '操作',
  ];

  const tableData = filteredTasks.map(task => [
    task.year,
    task.semester,
    task.batch,
    <span className="font-medium text-gray-800">
      {task.name}
    </span>,
    task.studentId,
    task.className,
    task.college,
    task.grade,
    task.status === '待评分' ? (
      <span className="text-gray-400">
        -
      </span>
    ) : (
      <span className="font-bold text-[#1890ff]">
        {task.score}
      </span>
    ),
    <span className={`px-2 py-0.5 rounded text-[12px] border ${task.status === '待评分' ? 'text-orange-500 bg-orange-50 border-orange-200' : 'text-green-600 bg-green-50 border-green-200'}`}>
      {task.status}
    </span>,
    task.status === '待评分' ? (
      <ActionLinks
        links={[
          {
            text: '评分',
            onClick: () => setScoreTask(task),
          },
        ]}
      />
    ) : (
      <span className="text-gray-400">
        -
      </span>
    ),
  ]);

  return (
    <div className="animate-fade-in flex flex-col h-full">
      {/* 顶部状态 Tab：切换全部、待评分、已评分 */}
      <div className="bg-white rounded-t shadow-sm border border-gray-100 p-4 pb-0 flex space-x-8 border-b">
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 cursor-pointer text-[14px] transition-colors ${activeTab === tab ? 'text-[#1890ff] border-b-2 border-[#1890ff] font-bold' : 'text-gray-600 hover:text-[#1890ff]'}`}
          >
            {tab}任务
          </button>
        ))}
      </div>

      {/* 结果容器：筛选、提示和表格放在同一卡片内 */}
      <div className="bg-white rounded-b shadow-sm border border-t-0 border-gray-100 p-4 flex-1">
        {/* 条件筛选卡片：按学年、批次、学院、被评人等条件查询 */}
        <FilterCard fields={filterFields} />

        {/* 风险提示条：已评分页签不展示 */}
        {activeTab !== '已评分' && (
          <div className="mb-4 bg-orange-50 border border-orange-100 px-4 py-3 rounded flex items-center">
            <div className="flex items-center text-[12px] text-orange-700">
              <AlertCircle size={16} className="mr-2" />
              <span>
                注意：同一批次请勿出现超过 50% 的 0分或1分 (极低分)，否则将被系统自动判定为恶意打分并作废。
              </span>
            </div>
          </div>
        )}

        {/* 我的互评任务表格 */}
        <TableLayout
          selectable={false}
          headers={tableHeaders}
          data={tableData}
        />

        {/* 分页区 */}
        <Pagination count={filteredTasks.length} />
      </div>

      {/* 小弹窗：给待评分学生打分 */}
      {scoreTask && (
        <ScoreModal
          task={scoreTask}
          onClose={() => setScoreTask(null)}
        />
      )}
    </div>
  );
}

// 互评评分弹窗
function ScoreModal({ task, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35">
      <div className="w-[520px] bg-white rounded shadow-[0_8px_28px_rgba(0,0,0,0.18)]">
        {/* 弹窗标题栏 */}
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-[15px] font-bold text-gray-900">
            互评评分
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* 被评学生基本信息 */}
        <div className="px-6 pt-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <InfoItem label="姓名" value={task.name} />
            <InfoItem label="学号" value={task.studentId} />
            <InfoItem label="班级" value={task.className} />
            <InfoItem label="学院" value={task.college} />
            <InfoItem label="学年" value={task.year} />
            <InfoItem label="学期" value={task.semester} />
          </div>
        </div>

        {/* 评分输入区 */}
        <div className="px-6 py-6">
          <div className="mb-4 px-3 py-3 bg-[#eaf4ff] border border-[#d3e8ff] rounded text-sm text-[#1677ff]">
            满分 5 分，请输入 0-5 之间的整数分。
          </div>

          <label className="flex items-center gap-3">
            <span className="text-sm text-gray-800 shrink-0">
              评分：
            </span>
            <input
              type="number"
              min="0"
              max="5"
              step="1"
              placeholder="请输入0-5之间的整数"
              onKeyDown={(event) => {
                if (event.key === '.' || event.key === 'e' || event.key === '-') {
                  event.preventDefault();
                }
              }}
              className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff]"
            />
          </label>
        </div>

        {/* 弹窗底部按钮 */}
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
}

// 基本信息展示项
function InfoItem({ label, value }) {
  return (
    <div>
      <div className="text-gray-400 mb-1">
        {label}
      </div>
      <div className="text-gray-800">
        {value}
      </div>
    </div>
  );
}
